import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import { createMerchantInvoice } from '../../api/merchantInvoices'

const BRAND_PURPLE = '#5B2EFF'
const BRAND_PURPLE_DARK = '#2A1B66'
const BRAND_ORANGE = '#FF8A00'
const SURFACE = 'rgba(27,16,64,0.96)'
const BORDER = '1px solid rgba(183,166,255,0.16)'

const currencyFromBusinessCountry = (country) => {
  const c = String(country || '').trim().toUpperCase()
  if (['SL', 'SLE', 'SIERRA LEONE'].includes(c)) return 'SLE'
  if (['LR', 'LRD', 'LIBERIA'].includes(c)) return 'LRD'
  if (['GN', 'GIN', 'GNF', 'GUINEA'].includes(c)) return 'GNF'
  if (['GM', 'GMD', 'GAMBIA'].includes(c)) return 'GMD'
  if (['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(c)) return 'USD'
  if (['UK', 'GB', 'GREAT BRITAIN', 'UNITED KINGDOM'].includes(c)) return 'GBP'
  if (['EU', 'FR', 'FRANCE', 'EUROPE', 'EUROPEAN UNION'].includes(c)) return 'EUR'
  if (['CA', 'CANADA'].includes(c)) return 'CAD'
  if (['AU', 'AUSTRALIA'].includes(c)) return 'AUD'
  return 'USD'
}

export default function MerchantInvoiceCreate() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { lang } = useLanguage()
  const isFr = lang === 'fr'

  const isBusiness = useMemo(() => {
    const role = (user?.role || user?.type || '').toLowerCase()
    return role === 'merchant' || role === 'business' || user?.account_type === 'business' || user?.is_business === true || !!user?.business_name
  }, [user])

  const currency = useMemo(() => {
    const businessCountry = user?.business_country || user?.businessCountry || user?.country || user?.profile?.country
    return currencyFromBusinessCountry(businessCountry)
  }, [user])

  const today = new Date()
  const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    customerName: '',
    customerContact: '',
    title: '',
    description: '',
    amount: '',
    notes: '',
    recurring: false,
    dueDate: nextWeek,
  })

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }))

  const submit = async () => {
    setError('')
    if (!form.customerName.trim()) return setError(isFr ? 'Le nom du client est obligatoire.' : 'Customer name is required.')
    if (!form.title.trim()) return setError(isFr ? 'Le titre de la facture est obligatoire.' : 'Invoice title is required.')
    if (Number(form.amount || 0) <= 0) return setError(isFr ? 'Entrez un montant valide.' : 'Enter a valid amount.')
    if (!form.dueDate) return setError(isFr ? 'La date limite est obligatoire.' : 'Due date is required.')

    setSaving(true)
    try {
      const baseRecord = {
        customer_name: form.customerName.trim(),
        customer_contact: form.customerContact.trim(),
        customer_email: form.customerContact.includes('@') ? form.customerContact.trim() : null,
        customer_phone: form.customerContact && !form.customerContact.includes('@') ? form.customerContact.trim() : null,
        invoice_title: form.title.trim(),
        description: form.description.trim(),
        amount: Number(form.amount || 0),
        currency,
        note: form.notes.trim(),
        due_date: form.dueDate,
        recurring: form.recurring,
        merchant_name: user?.business_name || user?.name || 'MojoPayr Business',
      }

      try {
        const response = await createMerchantInvoice({
          customer_name: baseRecord.customer_name,
          customer_email: baseRecord.customer_email,
          customer_phone: baseRecord.customer_phone,
          due_date: baseRecord.due_date,
          note: baseRecord.note,
          items: [
            {
              description: baseRecord.invoice_title,
              qty: 1,
              unit_price: baseRecord.amount,
            },
          ],
        })

        const invoice = response?.invoice || {}
        if (!invoice?.id) {
          throw new Error(isFr ? 'La facture a ete creee mais la reponse serveur est incomplete.' : 'Invoice was created but the server response was incomplete.')
        }
        navigate(`/invoices/${invoice.id}`)
      } catch (apiError) {
        setError(apiError?.response?.data?.message || apiError?.message || (isFr ? 'Impossible de creer la facture.' : 'Could not create the invoice.'))
      }
    } finally {
      setSaving(false)
    }
  }

  if (!isBusiness) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 6, background: SURFACE, border: BORDER }}>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ color: '#fff', fontWeight: 900 }}>
              {isFr ? 'Seuls les comptes entreprise peuvent creer des factures.' : 'Only business accounts can create invoices.'}
            </Typography>
          </CardContent>
        </Card>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Stack spacing={3}>
        <Card sx={{ borderRadius: 6, overflow: 'hidden', background: `linear-gradient(135deg, ${BRAND_PURPLE_DARK} 0%, ${BRAND_PURPLE} 100%)` }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 1 }}>
              {isFr ? 'Flux de facture professionnel' : 'Professional Invoice Flow'}
            </Typography>
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: 18, md: 22 }, lineHeight: 1.45 }}>
              {isFr
                ? 'Creez une demande de paiement, partagez-la avec le client, puis emettez un recu une fois le paiement confirme.'
                : 'Create a payment request, share it with the customer, and issue a receipt once payment is confirmed.'}
            </Typography>
          </CardContent>
        </Card>

        <Stack spacing={2.5}>
          {error ? <Typography sx={{ color: '#ff8a8a', fontWeight: 800 }}>{error}</Typography> : null}

          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 28, mb: 2 }}>
              {isFr ? 'Client' : 'Customer'}
            </Typography>
            <Stack spacing={2}>
              <TextField fullWidth placeholder={isFr ? 'Nom du client' : 'Customer Name'} value={form.customerName} onChange={(e) => setField('customerName', e.target.value)} />
              <TextField fullWidth placeholder={isFr ? 'Email ou telephone' : 'Email or Phone'} value={form.customerContact} onChange={(e) => setField('customerContact', e.target.value)} />
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 28, mb: 2 }}>
              {isFr ? 'Details de la facture' : 'Invoice Details'}
            </Typography>
            <Stack spacing={2}>
              <TextField fullWidth placeholder={isFr ? 'Titre de la facture' : 'Invoice Title'} value={form.title} onChange={(e) => setField('title', e.target.value)} />
              <TextField fullWidth placeholder={isFr ? 'Description' : 'Description'} multiline minRows={4} value={form.description} onChange={(e) => setField('description', e.target.value)} />
              <TextField fullWidth placeholder={`${isFr ? 'Montant' : 'Amount'} (${currency})`} value={form.amount} onChange={(e) => setField('amount', e.target.value)} />
              <TextField fullWidth placeholder={isFr ? 'Notes (optionnel)' : 'Notes (optional)'} value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
              <FormControlLabel
                control={<Checkbox checked={form.recurring} onChange={(e) => setField('recurring', e.target.checked)} />}
                label={
                  <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 800 }}>
                      {isFr ? 'Enregistrer comme plan recurrent' : 'Save as recurring invoice plan'}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                      {isFr ? 'Utilisez ceci pour le loyer, les frais scolaires et la facturation repetitive.' : 'Use this for rent, school fees, service contracts, and repeat billing.'}
                    </Typography>
                  </Box>
                }
                sx={{ ml: 0, alignItems: 'flex-start' }}
              />
              <TextField fullWidth type="date" value={form.dueDate} onChange={(e) => setField('dueDate', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Stack>
          </Box>

          <Button
            variant="contained"
            onClick={submit}
            disabled={saving}
            sx={{
              borderRadius: 999,
              py: 1.6,
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: BRAND_ORANGE,
              color: '#0B0620',
              '&:hover': { bgcolor: '#ff9c2a' },
            }}
          >
            {saving ? (isFr ? 'Creation...' : 'Creating...') : (isFr ? 'Generer la facture' : 'Generate Invoice')}
          </Button>
        </Stack>
      </Stack>
    </Container>
  )
}
